// Sound, made on the spot with Web Audio from src/config/sound.ts. Handed
// to the views as a set of functions; tests hand in silence.

import { SOUND, type Bell } from "../config/sound.ts"

export type Sound = {
  readonly wake: () => void // call from a user gesture before any sound
  readonly setMuted: (muted: boolean) => void
  readonly lift: () => void
  readonly rest: (value: number, scale: number, width: number) => void
  readonly hold: (value: number, scale: number, width: number) => void
  readonly fold: (values: readonly number[], scale: number, width: number) => void
  readonly turn: () => void
  readonly refuse: () => void
  readonly monad: (width: number) => void
  readonly chime: (step: number) => void
}

const noop = (): void => undefined

export const silence = (): Sound => ({
  wake: noop,
  setMuted: noop,
  lift: noop,
  rest: noop,
  hold: noop,
  fold: noop,
  turn: noop,
  refuse: noop,
  monad: noop,
  chime: noop,
})

// One call to a sound, as data: what the teaser's film writes down while it
// plays silently, then renders with webSound (scripts/film-teaser.ts).
export type SoundCall =
  | { readonly kind: "lift" }
  | { readonly kind: "rest"; readonly value: number; readonly scale: number; readonly width: number }
  | { readonly kind: "hold"; readonly value: number; readonly scale: number; readonly width: number }
  | { readonly kind: "fold"; readonly values: readonly number[]; readonly scale: number; readonly width: number }
  | { readonly kind: "turn" }
  | { readonly kind: "refuse" }
  | { readonly kind: "monad"; readonly width: number }
  | { readonly kind: "chime"; readonly step: number }

export type Heard = { readonly at: number; readonly call: SoundCall } // at: ms since 1970

// Silent, but writes down every call with the time it was made.
export const recordingSound = (now: () => number, heard: Heard[]): Sound => {
  const note = (call: SoundCall): void => {
    heard.push({ at: now(), call })
  }
  return {
    wake: noop,
    setMuted: noop,
    lift: () => note({ kind: "lift" }),
    rest: (value, scale, width) => note({ kind: "rest", value, scale, width }),
    hold: (value, scale, width) => note({ kind: "hold", value, scale, width }),
    fold: (values, scale, width) => note({ kind: "fold", values: [...values], scale, width }),
    turn: () => note({ kind: "turn" }),
    refuse: () => note({ kind: "refuse" }),
    monad: (width) => note({ kind: "monad", width }),
    chime: (step) => note({ kind: "chime", step }),
  }
}

// Makes a written-down call sound.
export const playCall = (s: Sound, c: SoundCall): void => {
  switch (c.kind) {
    case "lift":
      return s.lift()
    case "rest":
      return s.rest(c.value, c.scale, c.width)
    case "hold":
      return s.hold(c.value, c.scale, c.width)
    case "fold":
      return s.fold(c.values, c.scale, c.width)
    case "turn":
      return s.turn()
    case "refuse":
      return s.refuse()
    case "monad":
      return s.monad(c.width)
    case "chime":
      return s.chime(c.step)
  }
}

// The pitch of a value. Folded rings read as zero, and a card's scale drops
// it by tritaves, so a folded card sounds lower and fuller.
export const pitchOf = (value: number, scale: number, width: number): number => {
  const span = width >= 4 ? 40 : SOUND.steps
  const steps = (value * SOUND.steps) / span
  return (SOUND.base * 3 ** (steps / SOUND.steps)) / 3 ** ((scale - 1) * 0.5)
}

type Audio = { readonly ctx: BaseAudioContext; readonly master: GainNode; readonly wet: ConvolverNode }

const impulse = (ctx: BaseAudioContext): AudioBuffer => {
  const rate = ctx.sampleRate
  const len = Math.floor(rate * SOUND.room.seconds)
  const buf = ctx.createBuffer(2, len, rate)
  for (let ch = 0; ch < 2; ch++) {
    const d = buf.getChannelData(ch)
    for (let i = 0; i < len; i++) d[i] = (Math.random() * 2 - 1) * (1 - i / len) ** SOUND.room.decay
  }
  return buf
}

// A struck bell: a sine carrier, an FM partial at the tritave, a soft upper
// sine. Sent dry and to the room.
const bell = (a: Audio, freq: number, when: number, b: Bell): void => {
  const { ctx } = a
  const out = ctx.createGain()
  out.gain.setValueAtTime(0, when)
  out.gain.linearRampToValueAtTime(b.gain, when + 0.006)
  out.gain.exponentialRampToValueAtTime(0.0008, when + b.length)
  const car = ctx.createOscillator()
  car.frequency.value = freq
  const mod = ctx.createOscillator()
  mod.frequency.value = freq * 3
  const modGain = ctx.createGain()
  modGain.gain.setValueAtTime(freq * 1.6 * b.bright, when)
  modGain.gain.exponentialRampToValueAtTime(freq * 0.05, when + b.length * 0.5)
  mod.connect(modGain).connect(car.frequency)
  const up = ctx.createOscillator()
  up.frequency.value = freq * 4.02
  const upGain = ctx.createGain()
  upGain.gain.setValueAtTime(0.18 * b.bright, when)
  upGain.gain.exponentialRampToValueAtTime(0.0005, when + b.length * 0.35)
  up.connect(upGain).connect(out)
  car.connect(out)
  out.connect(a.master)
  out.connect(a.wet)
  for (const o of [car, mod, up]) {
    o.start(when)
    o.stop(when + b.length + 0.05)
  }
}

const startDrone = (a: Audio): void => {
  const { ctx } = a
  const drone = ctx.createGain()
  drone.gain.value = 0
  drone.gain.linearRampToValueAtTime(SOUND.drone.gain, ctx.currentTime + SOUND.drone.fadeIn)
  const lp = ctx.createBiquadFilter()
  lp.type = "lowpass"
  lp.frequency.value = SOUND.drone.lowpass
  drone.connect(lp).connect(a.master)
  lp.connect(a.wet)
  const root = SOUND.base / 3 / 3
  for (const [mult, detune, g] of SOUND.drone.partials) {
    const o = ctx.createOscillator()
    o.type = "sine"
    o.frequency.value = root * mult
    o.detune.value = detune
    const og = ctx.createGain()
    og.gain.value = g
    const lfo = ctx.createOscillator()
    lfo.frequency.value = 0.05 + Math.random() * 0.07
    const lfoGain = ctx.createGain()
    lfoGain.gain.value = g * 0.5
    lfo.connect(lfoGain).connect(og.gain)
    o.connect(og).connect(drone)
    o.start()
    lfo.start()
  }
}

const open = (ctx: BaseAudioContext, muted: boolean): Audio => {
  const master = ctx.createGain()
  master.gain.value = muted ? 0 : SOUND.master
  const comp = ctx.createDynamicsCompressor()
  comp.threshold.value = SOUND.compressor.threshold
  comp.ratio.value = SOUND.compressor.ratio
  master.connect(comp).connect(ctx.destination)
  const wet = ctx.createConvolver()
  wet.buffer = impulse(ctx)
  const wetGain = ctx.createGain()
  wetGain.gain.value = SOUND.room.wet
  wet.connect(wetGain).connect(master)
  const a = { ctx, master, wet }
  startDrone(a)
  return a
}

const noise = (a: Audio): void => {
  const { ctx } = a
  const n = ctx.createBufferSource()
  const buf = ctx.createBuffer(1, Math.floor(ctx.sampleRate * SOUND.lift.seconds), ctx.sampleRate)
  const d = buf.getChannelData(0)
  for (let i = 0; i < d.length; i++) d[i] = (Math.random() * 2 - 1) * (1 - i / d.length) ** 4
  n.buffer = buf
  const bp = ctx.createBiquadFilter()
  bp.type = "bandpass"
  bp.frequency.value = SOUND.lift.band
  bp.Q.value = SOUND.lift.q
  const g = ctx.createGain()
  g.gain.value = SOUND.lift.gain
  n.connect(bp).connect(g).connect(a.master)
  n.start(ctx.currentTime)
}

const knock = (a: Audio): void => {
  const { ctx } = a
  const t = ctx.currentTime
  for (const [f, g] of SOUND.refuse.pitches) {
    const o = ctx.createOscillator()
    o.type = "triangle"
    o.frequency.setValueAtTime(f, t)
    o.frequency.exponentialRampToValueAtTime(f * 0.7, t + 0.25)
    const og = ctx.createGain()
    og.gain.setValueAtTime(g, t)
    og.gain.exponentialRampToValueAtTime(0.001, t + SOUND.refuse.seconds)
    o.connect(og).connect(a.master)
    o.start(t)
    o.stop(t + SOUND.refuse.seconds + 0.02)
  }
}

const tritave = (step: number): number => SOUND.base * 3 ** (step / SOUND.steps)

// What the page asks of the phone's audio. iOS plays Web Audio as "ambient"
// sound, and the silent switch mutes it; "playback" plays through the
// switch. Muted, the game hands the choice back ("auto"), so a silent game
// never stops the player's own music.
export type SessionKind = "playback" | "auto"

// `make` builds the audio context; a test passes an OfflineAudioContext.
// `session` asks the phone for a kind of audio; a test writes it down.
export const webSound = (make: () => BaseAudioContext | undefined, mutedAtStart: boolean, session: (kind: SessionKind) => void): Sound => {
  let audio: Audio | undefined
  let muted = mutedAtStart
  const live = (): Audio | undefined => (muted ? undefined : audio)
  const at = (a: Audio, delay: number): number => a.ctx.currentTime + delay
  return {
    wake: () => {
      session(muted ? "auto" : "playback")
      if (audio !== undefined) {
        // iOS also stops it as "interrupted" (a call, the phone locked)
        const stopped = audio.ctx.state !== "running" && audio.ctx.state !== "closed"
        if (typeof AudioContext === "function" && audio.ctx instanceof AudioContext && stopped) void audio.ctx.resume()
        return
      }
      const ctx = make()
      if (ctx !== undefined) audio = open(ctx, muted)
    },
    setMuted: (m) => {
      muted = m
      session(m ? "auto" : "playback")
      audio?.master.gain.setTargetAtTime(m ? 0 : SOUND.master, audio.ctx.currentTime, 0.08)
    },
    lift: () => {
      const a = live()
      if (a !== undefined) noise(a)
    },
    rest: (value, scale, width) => {
      const a = live()
      if (a !== undefined) bell(a, pitchOf(value, scale, width), at(a, 0.01), SOUND.rest)
    },
    hold: (value, scale, width) => {
      const a = live()
      if (a !== undefined) bell(a, pitchOf(value, scale, width) / 3, at(a, 0.01), SOUND.hold)
    },
    fold: (values, scale, width) => {
      const a = live()
      if (a === undefined) return
      const t = at(a, 0.02)
      values.forEach((v, i) => bell(a, pitchOf(v, scale, width), t + i * SOUND.fold.spread, SOUND.fold.note))
      const mid = pitchOf(values[1] ?? 0, scale + 1, width)
      bell(a, mid, t + 0.34, SOUND.fold.landing)
      bell(a, mid * 3, t + 0.36, SOUND.fold.over)
    },
    turn: () => {
      const a = live()
      if (a === undefined) return
      const t = at(a, 0.02)
      for (let i = 0; i < SOUND.turn.notes; i++) bell(a, tritave(6 - i * 2), t + i * SOUND.turn.gap, SOUND.turn.bell)
    },
    refuse: () => {
      const a = live()
      if (a !== undefined) knock(a)
    },
    monad: (width) => {
      const a = live()
      if (a === undefined) return
      const t = at(a, 0.05)
      const m = (3 ** width - 1) / 2
      const step = Math.max(1, Math.round(m / SOUND.steps))
      let k = 0
      for (let v = -m; v <= m; v += step) bell(a, pitchOf(v, 1, width), t + k++ * SOUND.monad.gap, SOUND.monad.note)
      const end = t + k * SOUND.monad.gap + 0.2
      for (const s of SOUND.monad.chord) bell(a, tritave(s) / 3, end, SOUND.monad.chordBell)
    },
    chime: (step) => {
      const a = live()
      if (a !== undefined) bell(a, tritave(step), at(a, 0.01), SOUND.chime)
    },
  }
}

// Safari's audio session (navigator.audioSession, from Safari 16.4) is not
// in TypeScript's DOM types, so it is looked up by name. Other browsers have
// none and are left alone.
const askSession = (kind: SessionKind): void => {
  const audioSession: unknown = Reflect.get(navigator, "audioSession")
  if (typeof audioSession === "object" && audioSession !== null && "type" in audioSession) Reflect.set(audioSession, "type", kind)
}

export const browserSound = (muted: boolean): Sound =>
  webSound(() => (typeof AudioContext === "function" ? new AudioContext() : undefined), muted, askSession)
